import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
  Trash2,
  Bot,
  ShieldCheck,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PATH_CONTEXT = {
  "/": "dashboard",
  "/forge": "forge",
  "/calendar": "calendar",
  "/quran": "quran",
  "/settings": "settings",
};

const PROVIDER_META = {
  groq: { label: "Groq", dot: "bg-emerald-500", badge: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  deepseek: { label: "DeepSeek", dot: "bg-indigo-500", badge: "border-indigo-200 bg-indigo-50 text-indigo-700" },
};

export default function ChatBubble() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("user"); // "user" | "admin"
  const [provider, setProvider] = useState("groq");
  const [providers, setProviders] = useState([]);
  const [messages, setMessages] = useState([]); // {role, content, id?}
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const pageContext = PATH_CONTEXT[location.pathname] || "dashboard";

  useEffect(() => {
    if (!open || providers.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get("/chat/providers");
        setProviders(data || []);
        // Prefer Groq if configured, else first configured provider
        const groq = (data || []).find((p) => p.id === "groq" && p.configured);
        const firstConfigured = groq || (data || []).find((p) => p.configured);
        if (firstConfigured) setProvider(firstConfigured.id);
      } catch (e) {
        // ignore
      }
    })();
  }, [open, providers.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  // Reset chat when switching scope
  useEffect(() => {
    setMessages([]);
    setSessionId(null);
    setError("");
  }, [scope]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setError("");
    setInput("");
    setLoading(true);
    const optimisticUser = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, optimisticUser]);
    try {
      const endpoint = scope === "admin" ? "/chat/admin" : "/chat/user";
      const payload = {
        message: text,
        provider,
        session_id: sessionId || undefined,
        page_context: scope === "user" ? pageContext : undefined,
      };
      const { data } = await api.post(endpoint, payload);
      setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimisticUser.id),
        data.user_message,
        data.assistant_message,
      ]);
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id));
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
    setError("");
  };

  const currentProviderMeta = PROVIDER_META[provider] || PROVIDER_META.groq;

  const suggestionPrompts = useMemo(() => {
    if (scope === "admin") {
      return [
        "Summarise today's workspace activity.",
        "Which modules have no recent content?",
        "Draft a welcome email for new users.",
      ];
    }
    if (pageContext === "forge") {
      return [
        "Pick a template for a freelancer portfolio.",
        "Suggest 3 names for my task board app.",
        "What's a good first project to forge?",
      ];
    }
    if (pageContext === "calendar") {
      return [
        "Draft a team standup event for tomorrow 9am.",
        "What's a good café in Berlin for a 1:1?",
        "Plan a 3-session study week.",
      ];
    }
    if (pageContext === "quran") {
      return [
        "Explain Al-Fatiha verse 6 in simple words.",
        "Which surahs cover mercy?",
        "Summarise Al-Asr in 2 lines.",
      ];
    }
    return [
      "What can I do in FullEcosystem?",
      "Give me a quick-start for the forge.",
      "Show me one useful shortcut.",
    ];
  }, [scope, pageContext]);

  if (user === null || user === false) return null;

  return (
    <>
      {/* Floating toggle */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-bubble-toggle"
          className="fixed bottom-20 right-6 z-40 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-slate-900 text-white shadow-lg hover:shadow-xl hover:bg-slate-800 transition-all duration-200"
        >
          <div className="h-7 w-7 rounded-full bg-white flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5 text-slate-900" />
          </div>
          <div className="text-left leading-tight">
            <div className="text-sm font-semibold">Ask Fuse</div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-white/60">
              AI assistant
            </div>
          </div>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-20 right-6 z-40 w-[360px] sm:w-[420px] max-w-[calc(100vw-2rem)] h-[min(620px,calc(100vh-6rem))] bg-white border border-slate-200 rounded-md shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-900 text-white">
            <div className="h-8 w-8 rounded-md bg-white flex items-center justify-center shrink-0">
              {scope === "admin" ? (
                <ShieldCheck className="h-4 w-4 text-slate-900" />
              ) : (
                <Sparkles className="h-4 w-4 text-slate-900" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-semibold leading-tight">
                {scope === "admin" ? "Atlas · Admin copilot" : "Fuse · Ecosystem assistant"}
              </div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-white/60 truncate">
                {scope === "user" ? `context: ${pageContext}` : "admin workspace view"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
              data-testid="chat-close"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Controls */}
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <div className="flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="chat-scope-toggle">
                <button
                  type="button"
                  onClick={() => setScope("user")}
                  data-testid="chat-scope-user"
                  className={`text-xs px-2.5 py-1 rounded-sm transition-colors ${
                    scope === "user" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                >
                  User
                </button>
                <button
                  type="button"
                  onClick={() => setScope("admin")}
                  data-testid="chat-scope-admin"
                  className={`text-xs px-2.5 py-1 rounded-sm transition-colors ${
                    scope === "admin" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                >
                  Admin
                </button>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="chat-provider-trigger"
                  className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider rounded-md border px-2.5 py-1 ${currentProviderMeta.badge}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${currentProviderMeta.dot}`} />
                  {currentProviderMeta.label}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">
                  AI provider
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {providers.map((p) => (
                  <DropdownMenuItem
                    key={p.id}
                    onClick={() => setProvider(p.id)}
                    disabled={!p.configured}
                    data-testid={`chat-provider-${p.id}`}
                    className="cursor-pointer"
                  >
                    <span className={`h-1.5 w-1.5 rounded-full mr-2 ${PROVIDER_META[p.id]?.dot || "bg-slate-400"}`} />
                    <div className="flex-1">
                      <div className="text-sm">{p.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {p.default_model}
                      </div>
                    </div>
                    {!p.configured && (
                      <span className="text-[10px] text-slate-400">off</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {messages.length > 0 && (
              <button
                type="button"
                onClick={resetChat}
                data-testid="chat-reset"
                className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
              >
                <Trash2 className="h-3 w-3" /> Clear
              </button>
            )}
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white"
            data-testid="chat-messages"
          >
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Bot className="h-4 w-4 text-slate-700" />
                    <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
                      Welcome
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {scope === "admin"
                      ? `Hi ${user?.name}. Ask about users, creations, events, or anything about the live workspace.`
                      : `Hi ${user?.name}. I can help across App Forge, Calendar·Maps, and Quran Connections.`}
                  </p>
                </div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
                  Try
                </div>
                <div className="space-y-2">
                  {suggestionPrompts.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setInput(p)}
                      data-testid={`chat-suggestion-${p.slice(0, 10).replace(/\s+/g, '-')}`}
                      className="w-full text-left text-sm rounded-md border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 px-3 py-2 text-slate-700 transition-all duration-150"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={m.id || i}
                data-testid={`chat-message-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-md px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-800 border border-slate-200"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="chat-loading">
                <div className="rounded-md bg-slate-100 border border-slate-200 px-3 py-2 inline-flex items-center gap-2 text-xs text-slate-600">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}

            {error && (
              <div
                data-testid="chat-error"
                className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700"
              >
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-end gap-2"
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder={scope === "admin" ? "Ask about the workspace…" : "Ask Fuse anything…"}
                data-testid="chat-input"
                className="flex-1 resize-none rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:border-slate-900 focus:bg-white transition-colors max-h-32"
              />
              <Button
                type="submit"
                size="icon"
                disabled={loading || !input.trim()}
                data-testid="chat-send-button"
                className="shrink-0 h-10 w-10 bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
            <div className="mt-2 flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-slate-400">
              <MessageSquare className="h-3 w-3" />
              <span>Enter to send · Shift+Enter for newline</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
